import EmptyState from '@/components/EmptyState';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import { ChannelHoursContent } from '@/features/opening-hours/components/ChannelHoursView';
import { useWeeklyHoursEditor } from '@/features/opening-hours/components/WeeklyHoursEditor';
import { useOpeningHours } from '@/features/opening-hours/hooks/useOpeningHours';
import { useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { Block, Button, Navbar, Page, Preloader, Segmented } from 'framework7-react';
import { useState } from 'react';

type OrderService = 'PICKUP' | 'DELIVERY';

/**
 * Pickup and delivery opening hours merged into one settings page. A segmented
 * control switches the active channel; the weekly editor + Navbar Save action
 * track whichever channel is selected (one bulk PUT per channel, same as the
 * standalone `ChannelHoursView`). Reservation hours stay on their own page.
 */
export default function OrderHoursPage() {
  const t = useT();
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: rows, isLoading, isError } = useOpeningHours();
  const [service, setService] = useState<OrderService>('PICKUP');

  // Re-seeds whenever `service` (or the server data) changes - see
  // useWeeklyHoursEditor - so switching tabs loads that channel's schedule.
  const editor = useWeeklyHoursEditor(service, rows ?? []);
  const isCustom = !!rows?.some((r) => r.service === service);

  return (
    <Page name="order-hours">
      <Navbar title={t.openingHours.orderHoursTitle} backLink={settingsBackLink}>
        {!isLoading && !isError && rows && isCustom && (
          <SettingsSaveNav
            isDirty={editor.isDirty}
            saving={editor.isPending}
            onSave={editor.handleSave}
          />
        )}
      </Navbar>

      <Block>
        <Segmented strong>
          <Button active={service === 'PICKUP'} onClick={() => setService('PICKUP')}>
            {t.openingHours.pickupHoursTitle}
          </Button>
          <Button active={service === 'DELIVERY'} onClick={() => setService('DELIVERY')}>
            {t.openingHours.deliveryHoursTitle}
          </Button>
        </Segmented>
      </Block>

      {isLoading && (
        <Block style={{ textAlign: 'center', marginTop: 40 }}>
          <Preloader />
        </Block>
      )}

      {!isLoading && (isError || !rows) && (
        <Block>
          <EmptyState
            icon="exclamationmark_triangle"
            title={t.openingHours.couldNotLoadHours}
            description={t.openingHours.checkConnection}
          />
        </Block>
      )}

      {!isLoading && !isError && rows && (
        <ChannelHoursContent service={service} rows={rows} editor={editor} />
      )}
    </Page>
  );
}
